/**
 * 登録済み 360度写真 (provider='upload-360') の 3D パノラマ重畳 (docs/27 §6-3)。
 *
 * Google の `StreetViewPanorama` (StreetSurveyPanorama.tsx) の代わりに shared-ui の `PanoramaViewer`
 * で自前の全天球画像を見回し、`renderOverlay` で検出要素のピンを重ねる。ピンの方位・仰角は
 * 切り出し画像 (baseHeading + imageIndex×90、fov 90、4:3) 上の box2d から `azElFromPerspectiveBox`
 * (厳密投影) で逆算し、現在の視点へ `projectAzElToPerspective` で投影する。ピンの色・絵文字・
 * tooltip は Google 版と同じ解決関数を使う (凡例と一致)。
 */
import { useMemo } from 'react';
import { Card, PanoramaViewer, type PanoramaViewportState } from '@citizen-walk/shared-ui';
import { azElFromPerspectiveBox, projectAzElToPerspective } from '@citizen-walk/shared-types';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import { useStoragePhotoUrl } from './useStoragePhotoUrl';
import { computeVerticalCenterRatio } from './streetViewGeometry';
import {
  resolvePinColor,
  resolvePinRoleColor,
  resolvePinRoleEmoji,
  resolvePinTooltip,
} from './streetSurveyPinDisplay';

const GLOW_BASE_SIZE_PX = 90;
const GLOW_SCALE_MIN = 0.6;
const GLOW_SCALE_MAX = 1.4;
const GLOW_SCALE_NEAR_M = 5;
const GLOW_SCALE_FAR_M = 40;

function resolveGlowScale(distanceMeters: number): number {
  const t = Math.min(
    1,
    Math.max(0, (distanceMeters - GLOW_SCALE_NEAR_M) / (GLOW_SCALE_FAR_M - GLOW_SCALE_NEAR_M)),
  );
  return GLOW_SCALE_MAX - t * (GLOW_SCALE_MAX - GLOW_SCALE_MIN);
}

function parseAspect(size: string | undefined): number {
  const m = /^(\d+)x(\d+)$/.exec((size ?? '').trim());
  if (!m) return 4 / 3;
  const w = Number(m[1]);
  const h = Number(m[2]);
  return w > 0 && h > 0 ? w / h : 4 / 3;
}

export interface StreetSurveyUploadPanoramaProps {
  post: StreetSurveyPost;
  primaryAxis: PrimaryAxisResolution;
  roleTypeAxis?: RoleTypeAxisResolution;
  highlightedRank?: number | null;
  onSelectRank?: (rank: number) => void;
  reasoningField?: string;
  /** 解析前のプレビュー等、ピンを重ねない場合 true。 */
  hidePins?: boolean;
  className?: string;
}

export function StreetSurveyUploadPanorama({
  post,
  primaryAxis,
  roleTypeAxis,
  highlightedRank,
  onSelectRank,
  reasoningField,
  hidePins = false,
  className = 'w-full h-[500px] rounded-lg border border-slate-200',
}: StreetSurveyUploadPanoramaProps) {
  const streetView = post.streetView;
  const photo = useStoragePhotoUrl(post.photoPath, post.photoUrl);

  // 各ピンの方位・仰角 (視点に依らない) を先に解いておく。
  const pinDirections = useMemo(() => {
    if (!streetView || hidePins) return [];
    const classifiedRanks = new Set(post.classifications.map((c) => c.rank));
    const hfov = streetView.imageParams.fov;
    const aspect = parseAspect(streetView.imageParams.size);
    return streetView.pins
      .filter((pin) => classifiedRanks.has(pin.rank))
      .map((pin) => {
        const heading =
          streetView.imageParams.headings[pin.imageIndex] ??
          (streetView.baseHeading + pin.imageIndex * 90) % 360;
        const { azimuthDeg, elevationDeg } = azElFromPerspectiveBox(pin.box2d, {
          headingDeg: heading,
          pitchDeg: streetView.imageParams.pitch,
          hfovDeg: hfov,
          aspect,
        });
        const emoji = roleTypeAxis ? resolvePinRoleEmoji(post, roleTypeAxis, pin.rank) : undefined;
        const fillColor =
          (roleTypeAxis ? resolvePinRoleColor(post, roleTypeAxis, pin.rank) : undefined) ??
          resolvePinColor(post, primaryAxis, pin.rank);
        return {
          rank: pin.rank,
          azimuthDeg,
          elevationDeg,
          emoji,
          fillColor,
          glowSizePx: GLOW_BASE_SIZE_PX * resolveGlowScale(pin.distanceMeters),
          title: `${resolvePinTooltip(post, primaryAxis, pin.rank, roleTypeAxis, reasoningField)} / 検出位置: 画像内上から${Math.round(computeVerticalCenterRatio(pin.box2d) * 100)}% / 距離${pin.distanceMeters}m`,
        };
      });
  }, [streetView, post, primaryAxis, roleTypeAxis, reasoningField, hidePins]);

  if (!streetView) return null;

  if (photo.status === 'error') {
    return (
      <Card padding="md" className="border-danger-600">
        <p className="text-sm text-danger-700">360度写真の読込に失敗しました: {photo.error}</p>
      </Card>
    );
  }

  const renderOverlay = (state: PanoramaViewportState) =>
    pinDirections.map((p) => {
      const proj = projectAzElToPerspective(p.azimuthDeg, p.elevationDeg, {
        headingDeg: state.heading,
        pitchDeg: state.pitch,
        hfovDeg: state.hfov,
        aspect: state.width / Math.max(1, state.height),
      });
      if (!proj.visible) return null;
      const isHighlighted = p.rank === highlightedRank;
      return (
        <div
          key={p.rank}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: proj.sx * state.width, top: proj.sy * state.height }}
        >
          {isHighlighted && (
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                width: p.glowSizePx,
                height: p.glowSizePx,
                background: `radial-gradient(ellipse at center, ${p.fillColor} 0%, ${p.fillColor} 45%, transparent 85%)`,
                opacity: 0.8,
              }}
            />
          )}
          <button
            type="button"
            title={p.title}
            onClick={(e) => {
              e.stopPropagation();
              onSelectRank?.(p.rank);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="relative flex items-center justify-center rounded-full text-white text-[11px] font-bold cursor-pointer"
            style={{
              pointerEvents: 'auto',
              width: isHighlighted ? 26 : 20,
              height: isHighlighted ? 26 : 20,
              backgroundColor: p.fillColor,
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
            }}
          >
            {p.emoji ?? p.rank}
          </button>
        </div>
      );
    });

  return (
    <PanoramaViewer
      src={photo.url}
      baseHeadingDeg={streetView.baseHeading}
      initialPov={{ heading: streetView.baseHeading, pitch: 0, hfov: 90 }}
      className={className}
      renderOverlay={hidePins ? undefined : renderOverlay}
      alt="360度写真"
      data-testid="street-survey-upload-panorama"
    />
  );
}
